import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Downshift from 'downshift';
import classNames from 'classnames';
import { get } from 'lodash-es';
import {
  MIN_SEARCH_CHARS,
  SR_ANNOUNCE_DELAY_MS,
} from 'platform/site-wide/autosuggest/constants';
import {
  NO_MATCHING_CC_PROVIDER_FOUND,
  ServiceTypeValidationMessage,
} from '../../../constants';
import { normalizeSearchTerm } from '../../../utils/normalizeSearchTerm';

/**
 * CC Providers' Service Types Typeahead
 */
class CCServiceTypeAhead extends Component {
  constructor(props) {
    super(props);
    this.state = {
      services: [],
      statusMessage: '',
      // Controlled Downshift input value. We own it so the typed text is not
      // wiped when the field blurs without a selection (VACMS-20944 AC).
      inputValue: '',
    };
    this.statusTimer = null;
  }

  componentDidMount() {
    this.getServices();
  }

  componentDidUpdate() {
    // The list is loaded into Redux by ServicesLoadingOrShow, and this
    // component reads it from currentQuery. Sync it into local state once it
    // arrives. Previously the FETCH_SPECIALTIES loading gate forced an
    // unmount/remount so the mount-time read always saw populated data; a warm
    // specialties cache hit skips that gate, so the data can land right after
    // mount — without this a mount-only read leaves the typeahead empty.
    if (
      this.state.services.length === 0 &&
      this.props.currentQuery?.fetchSvcsRawData?.length
    ) {
      this.getServices();
    }
  }

  componentWillUnmount() {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
    }
  }

  getServices = async () => {
    const { currentQuery, initialSelectedServiceType } = this.props;
    const selectedItem =
      initialSelectedServiceType &&
      currentQuery?.fetchSvcsRawData?.find(
        ({ specialtyCode }) => specialtyCode === initialSelectedServiceType,
      );
    // Seed the controlled input with any pre-selected service name so it still
    // shows on mount (the controlled inputValue would otherwise start empty).
    const seededInputValue = selectedItem
      ? this.getSpecialtyName(selectedItem)
      : this.state.inputValue;
    this.setState({
      services: currentQuery?.fetchSvcsRawData || [],
      defaultSelectedItem: selectedItem,
      inputValue: seededInputValue,
    });
  };

  handleOnSelect = selectedItem => {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
    }
    // Reflect the chosen service in the controlled input, since a mouse click
    // can blur the field before Downshift's own value change reaches us.
    this.setState({
      statusMessage: '',
      inputValue: this.getSpecialtyName(selectedItem),
    });

    const value = selectedItem ? selectedItem.specialtyCode.trim() : null;
    this.props.handleServiceTypeChange({
      target: { value },
      selectedItem,
    });
  };

  optionClasses = selected => classNames('dropdown-option ', { selected });

  getSpecialtyName = specialty => {
    if (!specialty) return '';

    return specialty.name;
  };

  shouldShow = (input, specialty) => {
    if (!specialty) {
      return false;
    }
    const specialtyName = this.getSpecialtyName(specialty);
    if (!specialtyName) {
      return false;
    }
    // Normalize both the typed term and the specialty name so matching is
    // insensitive to leading/trailing and repeated whitespace, capitalization,
    // hyphens, and punctuation — the same treatment the VA health service
    // autosuggest applies via normalizeSearchTerm (VACMS-12922).
    const normalizedInput = normalizeSearchTerm(input);
    if (normalizedInput.length < MIN_SEARCH_CHARS) {
      return false;
    }
    return normalizeSearchTerm(specialtyName).includes(normalizedInput);
  };

  matchingServices = inputValue => {
    if (inputValue && this.state.services?.length) {
      return this.state.services.filter(specialty =>
        this.shouldShow(inputValue, specialty),
      );
    }
    return [];
  };

  updateStatusMessage = (matches, inputValue, isExpanded) => {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
    }

    if (!isExpanded || !inputValue) {
      this.setState({ statusMessage: '' });
      return;
    }

    if (matches && matches.length > 0) {
      this.statusTimer = setTimeout(() => {
        this.setState({
          statusMessage: `${matches.length} result${
            matches.length === 1 ? '' : 's'
          } for ${inputValue}`,
        });
      }, SR_ANNOUNCE_DELAY_MS);
    } else {
      this.setState({ statusMessage: 'No results available.' });
    }
  };

  renderServiceTypeDropdownOptions = (
    getItemProps,
    highlightedIndex,
    matches,
    selectedServiceCode,
  ) => {
    const listboxClassName = `dropdown${
      this.props.isSmallDesktop ? ' drowpdown-fl-sm-desktop' : ''
    }`;

    // When the typed term matches nothing, show a non-selectable soft-warning
    // row inside the dropdown ("No matching providers found") rather than a
    // separate below-field prompt, mirroring the VA health autosuggest
    // (VACMS-20944, #22895). The id is kept so the input's aria-describedby and
    // existing selectors still resolve.
    if (matches.length === 0) {
      return (
        <ul
          id="service-typeahead-listbox"
          // `.dropdown` is a fixed 418px at desktop; the message this replaced
          // (MessagePromptDiv) carried vads-u-width--full to override that, so
          // keep it here or the box renders wider than the input.
          className={`${listboxClassName} vads-u-width--full`}
          role="listbox"
        >
          <li
            id="could-not-find-service-prompt"
            className="dropdown-option cc-no-matches-found"
            role="option"
            aria-selected="false"
            aria-disabled="true"
          >
            {`- ${NO_MATCHING_CC_PROVIDER_FOUND} -`}
          </li>
        </ul>
      );
    }

    return (
      <ul
        id="service-typeahead-listbox"
        className={listboxClassName}
        role="listbox"
      >
        {matches.map((specialty, index) => (
          <li
            key={`${this.getSpecialtyName(specialty)}-${index}`}
            {...getItemProps({
              item: specialty,
              className: this.optionClasses(index === highlightedIndex),
              role: 'option',
              'aria-selected': specialty.specialtyCode === selectedServiceCode,
            })}
          >
            {this.getSpecialtyName(specialty)}
          </li>
        ))}
      </ul>
    );
  };

  render() {
    const { defaultSelectedItem, services } = this.state;
    if (!services) {
      return null;
    }
    const { showError, currentQuery, handleServiceTypeChange } = this.props;
    return (
      <Downshift
        onChange={this.handleOnSelect}
        selectedItem={!window.Cypress ? defaultSelectedItem : undefined}
        defaultSelectedItem={window.Cypress ? defaultSelectedItem : undefined}
        inputValue={this.state.inputValue}
        itemToString={this.getSpecialtyName}
        onInputValueChange={(inputValue, stateAndHelpers) => {
          // Downshift (v1) resets its input to the selected item's string on
          // blur and outer-click (`reset({ type: blurInput | mouseUp })`),
          // which would wipe the typed text. Because we control inputValue, we
          // simply don't adopt those reset changes, so the input keeps whatever
          // the user typed until they edit it (VACMS-20944 AC).
          const changeType = stateAndHelpers && stateAndHelpers.type;
          const isBlurReset =
            changeType === Downshift.stateChangeTypes.blurInput ||
            changeType === Downshift.stateChangeTypes.mouseUp;
          if (!isBlurReset) {
            this.setState({ inputValue });
          }
          const isExpanded =
            inputValue && inputValue.length >= MIN_SEARCH_CHARS;
          const matches = this.matchingServices(inputValue);
          this.updateStatusMessage(matches, inputValue, isExpanded);

          if (
            currentQuery.serviceType &&
            currentQuery.specialties &&
            inputValue !== currentQuery.specialties[currentQuery.serviceType]
          ) {
            handleServiceTypeChange({
              target: { value: '' },
              selectedItem: null,
            });
          }
        }}
      >
        {({
          getInputProps,
          getItemProps,
          getLabelProps,
          openMenu,
          isOpen,
          inputValue,
          highlightedIndex,
        }) => {
          const selectedServiceCode = get(
            this.props,
            'initialSelectedServiceType',
            null,
          );
          const showExpanded =
            isOpen && inputValue && MIN_SEARCH_CHARS <= inputValue.length;
          const matches = this.matchingServices(inputValue);
          return (
            <div
              id="service-error"
              className={classNames('vads-u-margin--0', {
                'usa-input-error': showError,
              })}
            >
              <label {...getLabelProps()} htmlFor="service-type-ahead-input">
                Select a provider type{' '}
                <span className="form-required-span">(*Required)</span>
              </label>
              <p className="service-hint-text">
                Start typing to search for a service, like Chiropractor or
                Optometrist.
              </p>
              {showError && (
                <span className="usa-input-error-message" role="alert">
                  <span id="error-message">
                    <span className="sr-only">Error</span>
                    {ServiceTypeValidationMessage}
                  </span>
                </span>
              )}
              <span id="service-typeahead">
                <input
                  role="combobox"
                  {...getInputProps({
                    placeholder: undefined,

                    onFocus: () => {
                      if (inputValue && inputValue.length >= MIN_SEARCH_CHARS) {
                        openMenu();
                      }
                    },
                    disabled: currentQuery?.fetchSvcsInProgress,
                  })}
                  id="service-type-ahead-input"
                  aria-describedby="could-not-find-service-prompt error-message"
                  aria-expanded={showExpanded ? 'true' : 'false'}
                  aria-controls="service-typeahead-listbox"
                />

                {showExpanded &&
                  this.renderServiceTypeDropdownOptions(
                    getItemProps,
                    highlightedIndex,
                    matches,
                    selectedServiceCode,
                  )}
              </span>
              <div className="usa-combo-box__status usa-sr-only" role="status">
                {this.state.statusMessage}
              </div>
            </div>
          );
        }}
      </Downshift>
    );
  }
}

CCServiceTypeAhead.propTypes = {
  handleServiceTypeChange: PropTypes.func.isRequired,
  currentQuery: PropTypes.object,
  initialSelectedServiceType: PropTypes.string,
  isSmallDesktop: PropTypes.bool,
  showError: PropTypes.bool,
  onBlur: PropTypes.func,
};

const mapDispatch = {};

const mapStateToProps = state => {
  return {
    currentQuery: state.searchQuery,
  };
};

export default connect(mapStateToProps, mapDispatch)(CCServiceTypeAhead);
